import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import useBreakpoint from '../../hooks/useBreakpoint';
import {
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Tag,
  Card,
  Empty,
  Tooltip,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  TeamOutlined,
  ReloadOutlined,
  SearchOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import PermissionGate from '../../components/auth/PermissionGate';
import { PERMISSIONS } from '../../config/permissions';
import { useAuth } from '../../contexts/AuthContext';
import basketService from '../../services/basketService';
import productService from '../../services/productService';

const VISIBILITY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'all', label: 'All partners' },
  { value: 'selected', label: 'Selected partners' },
];

const STATUS_COLORS = {
  ACTIVE: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
  INACTIVE: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', dot: 'bg-red-400' },
};

function BasketTileSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2.5 animate-pulse">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 bg-gray-200 rounded-md shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="h-3.5 bg-gray-200 rounded w-1/2 mb-1.5" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
        <div className="flex gap-1.5 shrink-0">
          <div className="h-6 bg-gray-200 rounded w-14" />
          <div className="h-6 bg-gray-200 rounded w-20" />
        </div>
      </div>
    </div>
  );
}

function BasketTile({ entry, onView, onVisibility, canEdit }) {
  const basket = entry?.basket ?? entry;
  const statusStyle = STATUS_COLORS[basket?.status] || STATUS_COLORS.ACTIVE;

  return (
    <div
      className="group relative bg-white border rounded-lg px-3 py-2.5 transition-all hover:shadow-sm border-gray-200 hover:border-green-200"
    >
      <span
        className={`inline-flex absolute top-0 right-4 -translate-y-1/2 z-10 items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border shadow-sm ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
      >
        <span className={`w-1 h-1 rounded-full ${statusStyle.dot}`} />
        {basket?.status || 'ACTIVE'}
      </span>

      <div className="flex flex-col gap-2 sm:hidden min-w-0">
        <h3
          className="font-semibold text-gray-900 text-sm break-words leading-snug min-w-0 pt-2"
          title={basket?.name}
        >
          {basket?.name || '-'}
        </h3>
        <div className="text-[11px] text-gray-600 line-clamp-2">{basket?.description || '-'}</div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="text-gray-600">
            ₹{Number(basket?.min_price || 0).toLocaleString()} min
          </span>
          <Tag color={basket?.visibility_type === 'all' ? 'green' : basket?.visibility_type === 'selected' ? 'blue' : 'default'}>
            {basket?.visibility_type || 'none'}
          </Tag>
          <span className="text-gray-500">{basket?.item_count ?? 0} items</span>
        </div>
        <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onView(entry)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-blue-600"
          >
            <EyeOutlined />
            <span>View</span>
          </button>
          {canEdit ? (
            <button
              type="button"
              onClick={() => onVisibility(entry)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-green-600"
            >
              <TeamOutlined />
              <span>Visibility</span>
            </button>
          ) : null}
        </div>
      </div>

      <div className="hidden sm:flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
          <span className="text-[10px] font-bold text-gray-400">
            {(basket?.name || '?').substring(0, 2).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="font-semibold text-gray-900 text-sm truncate leading-snug"
            title={basket?.name}
          >
            {basket?.name || '-'}
          </h3>
          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-600 truncate min-w-0">
            <span title={basket?.description}>{basket?.description || '-'}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] shrink-0">
            <span className="text-gray-600">
              ₹{Number(basket?.min_price || 0).toLocaleString()} min
            </span>
            <span className="text-gray-500">×{basket?.price_multiplier}</span>
            <Tag color={basket?.visibility_type === 'all' ? 'green' : basket?.visibility_type === 'selected' ? 'blue' : 'default'}>
              {basket?.visibility_type || 'none'}
            </Tag>
            <span className="text-gray-500">{basket?.item_count ?? 0} items</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-1">
          <Tooltip title="View details">
            <button
              type="button"
              onClick={() => onView(entry)}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-colors"
            >
              <EyeOutlined />
              <span>View</span>
            </button>
          </Tooltip>
          {canEdit ? (
            <>
              <Tooltip title="Manage visibility">
                <button
                  type="button"
                  onClick={() => onVisibility(entry)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-gray-500 hover:bg-gray-100 hover:text-green-600 transition-colors"
                >
                  <TeamOutlined />
                  <span>Visibility</span>
                </button>
              </Tooltip>
              <Tooltip title={basket?.status === 'ACTIVE' ? 'Set Inactive' : 'Set Active'}>
                <button
                  type="button"
                  onClick={() => entry.onStatusToggle(entry)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${basket?.status === 'ACTIVE'
                      ? 'text-red-500 hover:bg-red-50'
                      : 'text-green-600 hover:bg-green-50'
                    }`}
                >
                  <ReloadOutlined />
                  <span>{basket?.status === 'ACTIVE' ? 'Hide' : 'Show'}</span>
                </button>
              </Tooltip>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function BasketManagementPage() {
  const PAGE_SIZE = 10;
  const { hasPermission, isSuperAdmin } = useAuth();
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRODUCT_BASKET_LISTINGS_EDIT);
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [nextPage, setNextPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const scrollContainerRef = useRef(null);
  const [scrollRoot, setScrollRoot] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [basketDetail, setBasketDetail] = useState(null);
  const [products, setProducts] = useState([]);
  const [ifas, setIfas] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingIfas, setLoadingIfas] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createForm] = Form.useForm();
  const [visibilityForm] = Form.useForm();

  const watchItems = Form.useWatch('items', createForm);
  const itemsPercentTotal = (watchItems ?? []).reduce(
    (sum, item) => sum + (Number(item?.percentage) || 0),
    0
  );
  const percentRemaining = Math.max(0, 100 - itemsPercentTotal);
  const percentOver = Math.max(0, itemsPercentTotal - 100);
  const isPercentValid = itemsPercentTotal === 100;

  const buildParams = useCallback(
    (page) => {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      return params;
    },
    [search, statusFilter]
  );

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await basketService.listBaskets(buildParams(1));
      if (result?.success && result?.data) {
        const data = result.data;
        const list = data.baskets ?? [];
        setBaskets(Array.isArray(list) ? list : []);
        const pag = data.pagination ?? {};
        const totalCount = pag.total ?? 0;
        const totalPages = Math.ceil(totalCount / (pag.limit || PAGE_SIZE)) || 1;
        setTotal(totalCount);
        setHasMore(1 < totalPages);
        setNextPage(2);
      } else {
        setBaskets([]);
      }
    } catch (err) {
      console.error('Error fetching baskets:', err);
      setError(err.response?.data?.message || 'Failed to load baskets');
      setBaskets([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || nextPage === 1) return;
    setLoadingMore(true);
    try {
      const result = await basketService.listBaskets(buildParams(nextPage));
      if (result?.success && result?.data) {
        const data = result.data;
        const list = data.baskets ?? [];
        const newList = Array.isArray(list) ? list : [];
        setBaskets((prev) => [...prev, ...newList]);
        const pag = data.pagination ?? {};
        const totalCount = pag.total ?? 0;
        const totalPages = Math.ceil(totalCount / (pag.limit || PAGE_SIZE)) || 1;
        const currentPage = pag.page ?? nextPage;
        setHasMore(currentPage < totalPages);
        setNextPage((p) => p + 1);
        if (pag.total != null) setTotal(pag.total);
      }
    } catch (err) {
      console.error('Error loading more baskets:', err);
      message.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextPage, loadingMore, loading]);

  useEffect(() => {
    fetchInitial();
  }, [search, statusFilter, fetchInitial]);

  const setScrollContainerRef = useCallback((el) => {
    scrollContainerRef.current = el;
    setScrollRoot(el);
  }, []);

  const { ref: sentinelRef, inView } = useInView({
    root: scrollRoot,
    rootMargin: '200px',
    threshold: 0,
  });

  useEffect(() => {
    if (inView && hasMore && !loadingMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loadingMore, loading, loadMore]);

  const fetchProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const result = await productService.getProducts({
        type: 'PRE_IPO',
        page: 1,
        limit: 200,
        sort_by: 'preference_index',
        sort_order: 'asc',
      });
      const data = result?.data ?? result;
      const list = data?.products ?? data?.data ?? data?.items ?? [];
      setProducts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching products:', err);
      message.error('Failed to load products');
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchIfas();
  }, [fetchProducts]);

  const fetchIfas = async () => {
    setLoadingIfas(true);
    try {
      const result = await basketService.listIfas({ limit: 500 });
      const data = result?.data ?? result;
      const list = data?.users ?? [];
      setIfas(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching partners:', err);
      message.error('Failed to load partners');
      setIfas([]);
    } finally {
      setLoadingIfas(false);
    }
  };

  const openCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      items: [
        { product_id: undefined, percentage: undefined },
        { product_id: undefined, percentage: undefined },
      ],
    });
    setCreateModalOpen(true);
  };

  const handleCreateBasket = async (values) => {
    const items = (values.items ?? []).filter((i) => i?.product_id && i?.percentage != null);
    if (items.length < 2) {
      message.error('Add at least 2 products with percentage');
      return;
    }
    if (items.length > 6) {
      message.error('Maximum 6 products allowed per basket');
      return;
    }
    const totalPct = items.reduce((s, i) => s + (Number(i.percentage) || 0), 0);
    if (Math.abs(totalPct - 100) > 0.01) {
      message.error('Item percentages must sum to 100');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: values.name?.trim(),
        description: values.description?.trim() || undefined,
        min_price: Number(values.min_price) || 0,
        price_multiplier: Number(values.price_multiplier) || 1,
        items: items.map((i) => ({
          product_id: i.product_id,
          percentage: Number(i.percentage),
        })),
        risks: (values.risks ?? []).filter((r) => r?.description?.trim()),
        reasons: (values.reasons ?? []).filter((r) => r?.description?.trim()),
      };
      const result = await basketService.createBasket(payload);
      if (result?.success) {
        message.success('Basket created successfully');
        setCreateModalOpen(false);
        createForm.resetFields();
        fetchInitial();
      } else {
        message.error(result?.message || 'Failed to create basket');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to create basket');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetailModal = async (record) => {
    const entry = record?.basket ? record : { basket: record, items: [], sector_weightage: [] };
    setSelectedBasket(entry);
    setBasketDetail(null);
    setDetailModalOpen(true);
    try {
      const uuid = entry.basket?.uuid ?? entry.uuid ?? record?.uuid;
      if (uuid) {
        const result = await basketService.getBasketById(uuid);
        if (result?.success && result?.data) {
          setBasketDetail(result.data);
        }
      } else {
        setBasketDetail(entry);
      }
    } catch (err) {
      console.error('Error fetching basket detail:', err);
      setBasketDetail(entry);
    }
  };

  const openVisibilityModal = async (record) => {
    if (!canEdit) return;
    const entry = record?.basket ? record : { basket: record };
    const uuid = entry.basket?.uuid ?? entry.uuid ?? record?.uuid;
    if (!uuid) return;
    setSelectedBasket(entry);
    setVisibilityModalOpen(true);
    await fetchIfas();
    try {
      const result = await basketService.getBasketById(uuid);
      const data = result?.data ?? {};
      const basket = data.basket ?? entry.basket ?? entry;
      const assignedIfas = data.assigned_ifas ?? [];
      const prefilledIds = assignedIfas.map((a) => a?.id || a?.uuid || a?.user_uuid).filter(Boolean);
      visibilityForm.setFieldsValue({
        visibility_type: basket.visibility_type || 'none',
        ifa_user_ids: prefilledIds,
      });
    } catch (err) {
      visibilityForm.setFieldsValue({
        visibility_type: entry.basket?.visibility_type ?? 'none',
        ifa_user_ids: [],
      });
    }
  };

  const handleUpdateVisibility = async (values) => {
    const entry = selectedBasket;
    const uuid = entry?.basket?.uuid ?? entry?.uuid;
    if (!uuid) return;
    const payload = {
      visibility_type: values.visibility_type,
    };
    if (values.visibility_type === 'selected' && values.ifa_user_ids?.length) {
      payload.ifa_user_ids = values.ifa_user_ids;
    }
    setSubmitting(true);
    try {
      const result = await basketService.updateBasketVisibility(uuid, payload);
      if (result?.success !== false) {
        message.success('Visibility updated successfully');
        setVisibilityModalOpen(false);
        visibilityForm.resetFields();
        fetchInitial();
      } else {
        message.error(result?.message || 'Failed to update visibility');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update visibility');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (record) => {
    if (!canEdit) return;
    const entry = record?.basket ? record : { basket: record };
    const basket = entry.basket;
    const currentStatus = basket?.status || 'ACTIVE';
    const nextStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    try {
      const result = await basketService.updateBasketStatus(basket.uuid, nextStatus);
      if (result?.success) {
        message.success(`Basket marked as ${nextStatus.toLowerCase()}`);
        fetchInitial();
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const filteredBaskets = baskets;
  const isAboveSm = useBreakpoint('sm');
  const [windowWidth, setWindowWidth] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth : 1024)
  );
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const drawerWidth = isAboveSm ? 520 : Math.min(windowWidth - 24, 520);
  const detailDrawerWidth = isAboveSm ? 480 : Math.min(windowWidth - 24, 480);
  const visibilityDrawerWidth = isAboveSm ? 440 : Math.min(windowWidth - 24, 440);

  const detail = basketDetail ?? selectedBasket;
  const detailBasket = detail?.basket ?? detail;
  const detailItems = detail?.items ?? selectedBasket?.items ?? [];
  const sectorWeightage = detail?.sector_weightage ?? selectedBasket?.sector_weightage ?? [];

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-x-hidden overflow-y-auto min-h-0 flex flex-col flex-1 h-full">
      <div className="shrink-0">
        <PageBreadcrumbs />
      </div>

      <div className="p-4 sm:p-0 flex flex-col flex-1 min-h-0 overflow-x-hidden overflow-y-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-0 sm:pb-0 min-w-0">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5 shrink-0">
          <div className="flex items-center gap-2 flex-1">
            <Input.Search
              placeholder="Search baskets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={handleSearchChange}
              allowClear
              className="max-w-xs"
              prefix={<SearchOutlined className="text-gray-400" />}
            />
            <Select
              placeholder="All Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              className="w-32"
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
          </div>

          <div className="flex items-center gap-2">
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchInitial}
                loading={loading}
              />
            </Tooltip>
            <PermissionGate permission={PERMISSIONS.PRODUCT_BASKET_LISTINGS_CREATE}>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                Create Basket
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center mb-4">
            <p className="text-red-600 text-sm">{error}</p>
            <Button size="small" onClick={fetchInitial} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && (
          <div className="grid gap-4">
            {[...Array(5)].map((_, i) => (
              <BasketTileSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredBaskets.length === 0 && (
          <div className="flex items-center justify-center min-h-[350px]">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-gray-500">
                  {search || statusFilter
                    ? 'No baskets match your filters'
                    : 'No baskets yet'}
                </span>
              }
            >
              {!search && !statusFilter && (
                <PermissionGate permission={PERMISSIONS.PRODUCT_BASKET_LISTINGS_CREATE}>
                  <Button type="primary" onClick={openCreateModal}>
                    Create First Basket
                  </Button>
                </PermissionGate>
              )}
            </Empty>
          </div>
        )}

        {/* Baskets: scroll area + footer */}
        {!loading && !error && filteredBaskets.length > 0 && (
          <div className="flex-1 min-h-0 -mx-4 sm:-mx-6 mt-2 flex flex-col overflow-hidden">
            <div
              ref={setScrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6"
            >
              <div className="grid gap-4 pt-3 pb-4">
                {filteredBaskets.map((entry) => {
                  const basket = entry?.basket ?? entry;
                  return (
                    <BasketTile
                      key={basket?.uuid}
                      entry={{ ...entry, onStatusToggle: handleStatusToggle }}
                      onView={openDetailModal}
                      onVisibility={openVisibilityModal}
                      canEdit={canEdit}
                    />
                  );
                })}
              </div>

              {hasMore && (
                <div ref={sentinelRef} className="h-4 shrink-0" aria-hidden="true" />
              )}
            </div>

            <div className="flex items-center justify-between gap-3 py-3 px-4 sm:px-6 border-t border-gray-200 bg-white shrink-0">
              <p className="text-xs text-gray-600">
                {baskets.length} basket{baskets.length !== 1 ? 's' : ''}
                {total > 0 && total !== baskets.length && ` of ${total}`}
              </p>
              {loadingMore && (
                <Spin size="small" tip="Loading more…" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Basket Drawer */}
      <Drawer
        title="Create Basket"
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        width={drawerWidth}
        destroyOnClose
        placement="right"
        styles={{ body: { overflowX: 'hidden' } }}
        className="basket-drawer"
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateBasket}
          initialValues={{
            min_price: 50000,
            price_multiplier: 2,
            items: [
              { product_id: undefined, percentage: undefined },
              { product_id: undefined, percentage: undefined },
            ],
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Markets Leaders 2026" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="A basket of top pre-IPO companies" />
          </Form.Item>
          <Form.Item
            name="min_price"
            label="Min Price"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} className="w-full" />
          </Form.Item>
          <Form.Item
            name="price_multiplier"
            label="Price Multiplier"
            rules={[{ required: true }]}
          >
            <InputNumber min={0.1} step={0.1} className="w-full" />
          </Form.Item>
          <Form.Item
            label="Items"
            required
            extra={
              <span className="text-gray-500">
                Select 2–6 products from the Pre-IPO listings. Percentages must sum to 100%.
              </span>
            }
          >
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                        <div className="min-w-0 flex-1">
                          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                            Product
                          </p>
                          <Form.Item
                            {...restField}
                            name={[name, 'product_id']}
                            rules={[{ required: true, message: 'Select product' }]}
                            className="mb-0"
                          >
                            <Select
                              placeholder="Select product"
                              loading={loadingProducts}
                              showSearch
                              optionFilterProp="label"
                              filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())
                              }
                              options={products.map((p) => ({
                                value: p.uuid || p.id,
                                label: `${p.name || p.short_name || p.uuid} (${p.short_name || '-'})`,
                              }))}
                            />
                          </Form.Item>
                        </div>
                        <div className="w-full sm:w-32">
                          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                            Weight %
                          </p>
                          <Form.Item
                            {...restField}
                            name={[name, 'percentage']}
                            rules={[{ required: true, message: 'Required' }]}
                            className="mb-0"
                          >
                            <InputNumber
                              min={1}
                              max={100}
                              placeholder="%"
                              addonAfter="%"
                              className="w-full"
                            />
                          </Form.Item>
                        </div>
                        <div className="flex justify-end sm:pb-[2px]">
                          <Tooltip title="Remove item">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              disabled={fields.length <= 2}
                              className="flex h-8 w-8 items-center justify-center"
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      disabled={fields.length >= 6}
                    >
                      Add Item {fields.length >= 6 ? '(max 6)' : `(${fields.length}/6)`}
                    </Button>
                  </Form.Item>
                  <div
                    className={`mt-2 text-sm font-medium ${percentOver > 0
                        ? 'text-red-600'
                        : percentRemaining > 0
                          ? 'text-amber-600'
                          : 'text-green-600'
                      }`}
                  >
                    Total: {itemsPercentTotal}%
                    {percentOver > 0 ? (
                      <> — {percentOver}% over (max 100%)</>
                    ) : percentRemaining > 0 ? (
                      <> — {percentRemaining}% remaining</>
                    ) : (
                      <> — OK</>
                    )}
                  </div>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="Risks (Heading & Description)" className="mb-4">
            <Form.List name="risks">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex gap-2 mb-2 p-2 border border-gray-100 rounded bg-gray-50/50">
                      <div className="flex-1 space-y-2">
                        <Form.Item {...restField} name={[name, 'heading']} className="mb-0">
                          <Input size="small" placeholder="Heading (e.g. Market Risk)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          rules={[{ required: true, message: 'Required' }]}
                          className="mb-0"
                        >
                          <Input.TextArea size="small" rows={1} placeholder="Risk description" />
                        </Form.Item>
                      </div>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                    Add Risk
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="Reasons to Invest" className="mb-4">
            <Form.List name="reasons">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex gap-2 mb-2 p-2 border border-gray-100 rounded bg-gray-50/50">
                      <div className="flex-1 space-y-2">
                        <Form.Item {...restField} name={[name, 'heading']} className="mb-0">
                          <Input size="small" placeholder="Heading (e.g. Growth Potential)" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          rules={[{ required: true, message: 'Required' }]}
                          className="mb-0"
                        >
                          <Input.TextArea size="small" rows={1} placeholder="Reason description" />
                        </Form.Item>
                      </div>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                    </div>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="small">
                    Add Reason
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item className="mb-0">
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={!isPercentValid}
              >
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* Basket Detail Drawer */}
      <Drawer
        title={detailBasket?.name ? `Basket: ${detailBasket.name}` : 'Basket Details'}
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedBasket(null);
          setBasketDetail(null);
        }}
        footer={
          <PermissionGate permission={PERMISSIONS.PRODUCT_BASKET_LISTINGS_EDIT}>
            <Button
              type="primary"
              icon={<TeamOutlined />}
              onClick={() => {
                setDetailModalOpen(false);
                if (selectedBasket) openVisibilityModal(selectedBasket);
              }}
            >
              Manage Visibility
            </Button>
          </PermissionGate>
        }
        width={detailDrawerWidth}
        placement="right"
        styles={{ body: { overflowX: 'hidden' } }}
      >
        {detailBasket && (
          <div className="space-y-4 min-w-0 overflow-hidden">
            <Card size="small" title="Basket Info">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <dt className="text-gray-500">Description</dt>
                <dd className="break-words">{detailBasket.description || '-'}</dd>
                <dt className="text-gray-500">Min Price</dt>
                <dd>₹{Number(detailBasket.min_price || 0).toLocaleString()}</dd>
                <dt className="text-gray-500">Price Multiplier</dt>
                <dd>{detailBasket.price_multiplier}</dd>
                <dt className="text-gray-500">Basket Price</dt>
                <dd>₹{Number(detailBasket.basket_price || 0).toLocaleString()}</dd>
                <dt className="text-gray-500">Visibility</dt>
                <dd>
                  <Tag color={detailBasket.visibility_type === 'all' ? 'green' : detailBasket.visibility_type === 'selected' ? 'blue' : 'default'}>
                    {detailBasket.visibility_type || 'none'}
                  </Tag>
                </dd>
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <Tag color={detailBasket.status === 'ACTIVE' ? 'green' : 'default'}>
                    {detailBasket.status}
                  </Tag>
                </dd>
              </dl>
            </Card>
            <Card size="small" title="Items">
              <ul className="space-y-2">
                {detailItems.map((item, i) => (
                  <li key={item.uuid || i} className="flex justify-between text-sm">
                    <span>{item.product?.name || item.product_id || '-'}</span>
                    <span>{item.percentage}%</span>
                  </li>
                ))}
              </ul>
            </Card>
            {sectorWeightage?.length > 0 && (
              <Card size="small" title="Sector Weightage">
                <ul className="space-y-1">
                  {sectorWeightage.map((s, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span>{s.industry}</span>
                      <span>{s.weightage}%</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            {detail?.risks?.length > 0 && (
              <Card size="small" title="Basket Risks">
                <div className="space-y-3">
                  {detail.risks.map((r, i) => (
                    <div key={r.uuid || i} className="text-sm">
                      {r.heading && <div className="font-semibold text-gray-800">{r.heading}</div>}
                      <div className="text-gray-600 leading-relaxed">{r.description}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            {detail?.reasons?.length > 0 && (
              <Card size="small" title="Reasons to Invest">
                <div className="space-y-3">
                  {detail.reasons.map((r, i) => (
                    <div key={r.uuid || i} className="text-sm">
                      {r.heading && <div className="font-semibold text-gray-800">{r.heading}</div>}
                      <div className="text-gray-600 leading-relaxed">{r.description}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </Drawer>

      {/* Visibility Drawer */}
      <Drawer
        title="Update Basket Visibility"
        open={visibilityModalOpen}
        onClose={() => {
          setVisibilityModalOpen(false);
          visibilityForm.resetFields();
        }}
        width={visibilityDrawerWidth}
        destroyOnClose
        placement="right"
        styles={{ body: { overflowX: 'hidden' } }}
      >
        <Form
          form={visibilityForm}
          layout="vertical"
          onFinish={handleUpdateVisibility}
          initialValues={{ visibility_type: 'none', ifa_user_ids: [] }}
        >
          <Form.Item
            name="visibility_type"
            label="Visibility Type"
            rules={[{ required: true }]}
          >
            <Select options={VISIBILITY_OPTIONS} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.visibility_type !== curr.visibility_type}
          >
            {({ getFieldValue }) =>
              getFieldValue('visibility_type') === 'selected' ? (
                <Form.Item
                  name="ifa_user_ids"
                  label="Select partners"
                  rules={[{ required: true, message: 'Select at least one partner' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select partners"
                    loading={loadingIfas}
                    showSearch
                    optionFilterProp="label"
                    options={ifas.map((ifa) => ({
                      value: ifa.id,
                      label: `${ifa.name || ifa.email || ifa.id} (${ifa.email || '-'})`,
                    }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item className="mb-0 mt-4">
            <Space>
              <Button
                onClick={() => {
                  setVisibilityModalOpen(false);
                  visibilityForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Update Visibility
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
